import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Plug, Plus, Trash2, Play, Check, X, FileJson, ChevronDown, ChevronUp, Edit2, Save } from 'lucide-react';

const ACCENT = '#f97316';

interface TestResult {
  action: string;
  success: boolean;
  message: string;
  tested_at: string;
}

interface ConnectorAction {
  id: string;
  name: string;
  description: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  headers: Record<string, string>;
  query_params: { name: string; type: string; required: boolean }[];
  body_schema?: Record<string, unknown>;
  response_mapping: { source_path: string; target_field: string }[];
  pagination?: { type: 'offset' | 'cursor' | 'page'; param_name: string; size_param?: string; default_size?: number };
}

interface CustomConnector {
  id: string;
  name: string;
  description: string;
  base_url: string;
  auth: { type: string; config: Record<string, unknown> };
  actions: ConnectorAction[];
  status: 'draft' | 'active' | 'disabled';
  test_results?: TestResult[];
  created_at: string;
  updated_at: string;
}

const METHOD_COLORS: Record<string, string> = {
  GET: '#22c55e',
  POST: '#3b82f6',
  PUT: '#a855f7',
  PATCH: '#f97316',
  DELETE: '#ef4444',
};

const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'api_key', label: 'API Key' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'oauth2', label: 'OAuth 2.0' },
];

export default function CustomConnectorsPage() {
  const [connectors, setConnectors] = useState<CustomConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formBaseUrl, setFormBaseUrl] = useState('');
  const [formAuthType, setFormAuthType] = useState('none');
  const [formAuthConfig, setFormAuthConfig] = useState<Record<string, string>>({});
  const [formActions, setFormActions] = useState<ConnectorAction[]>([]);
  const [formStatus, setFormStatus] = useState<'draft' | 'active' | 'disabled'>('draft');
  const [saving, setSaving] = useState(false);

  // Import form state
  const [importUrl, setImportUrl] = useState('');
  const [importName, setImportName] = useState('');
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<CustomConnector | null>(null);

  const loadConnectors = useCallback(async () => {
    try {
      const data = await api.get<{ connectors: CustomConnector[] }>('/custom-connectors');
      setConnectors(data.connectors);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadConnectors(); }, [loadConnectors]);

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormBaseUrl('');
    setFormAuthType('none');
    setFormAuthConfig({});
    setFormActions([]);
    setFormStatus('draft');
    setEditingId(null);
  };

  const handleCreate = async () => {
    if (!formName.trim() || !formBaseUrl.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: formName,
        description: formDescription,
        base_url: formBaseUrl,
        auth: { type: formAuthType, config: formAuthConfig },
        actions: formActions,
        status: formStatus,
      };

      if (editingId) {
        await api.patch(`/custom-connectors/${editingId}`, payload);
      } else {
        await api.post('/custom-connectors', payload);
      }

      resetForm();
      setShowCreate(false);
      await loadConnectors();
    } catch { /* empty */ }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/custom-connectors/${id}`);
      setConfirmDelete(null);
      await loadConnectors();
    } catch { /* empty */ }
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      await api.post(`/custom-connectors/${id}/test`);
      await loadConnectors();
    } catch { /* empty */ }
    setTesting(null);
  };

  const handleEdit = (connector: CustomConnector) => {
    setFormName(connector.name);
    setFormDescription(connector.description);
    setFormBaseUrl(connector.base_url);
    setFormAuthType(connector.auth.type);
    setFormAuthConfig(connector.auth.config as Record<string, string>);
    setFormActions(connector.actions);
    setFormStatus(connector.status);
    setEditingId(connector.id);
    setShowCreate(true);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const payload: Record<string, unknown> = {};
      if (importUrl) payload.spec_url = importUrl;
      if (importJson) payload.spec_json = JSON.parse(importJson);
      if (importName) payload.name = importName;

      const result = await api.post<{ connector: CustomConnector }>('/custom-connectors/import-openapi', payload);
      setImportPreview(result.connector);
    } catch { /* empty */ }
    setImporting(false);
  };

  const handleSaveImported = async () => {
    if (!importPreview) return;
    setSaving(true);
    try {
      await api.post('/custom-connectors', {
        name: importPreview.name,
        description: importPreview.description,
        base_url: importPreview.base_url,
        auth: importPreview.auth,
        actions: importPreview.actions,
        status: 'draft',
      });
      setShowImport(false);
      setImportPreview(null);
      setImportUrl('');
      setImportJson('');
      setImportName('');
      await loadConnectors();
    } catch { /* empty */ }
    setSaving(false);
  };

  const addAction = () => {
    setFormActions(prev => [...prev, {
      id: crypto.randomUUID(),
      name: '',
      description: '',
      method: 'GET',
      path: '/',
      headers: {},
      query_params: [],
      response_mapping: [],
    }]);
  };

  const updateAction = (index: number, field: string, value: unknown) => {
    setFormActions(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  const removeAction = (index: number) => {
    setFormActions(prev => prev.filter((_, i) => i !== index));
  };

  const addQueryParam = (actionIndex: number) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      query_params: [...a.query_params, { name: '', type: 'string', required: false }],
    } : a));
  };

  const updateQueryParam = (actionIndex: number, paramIndex: number, field: string, value: unknown) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      query_params: a.query_params.map((p, pi) => pi === paramIndex ? { ...p, [field]: value } : p),
    } : a));
  };

  const removeQueryParam = (actionIndex: number, paramIndex: number) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      query_params: a.query_params.filter((_, pi) => pi !== paramIndex),
    } : a));
  };

  const addResponseMapping = (actionIndex: number) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      response_mapping: [...a.response_mapping, { source_path: '', target_field: '' }],
    } : a));
  };

  const updateResponseMapping = (actionIndex: number, mapIndex: number, field: string, value: string) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      response_mapping: a.response_mapping.map((m, mi) => mi === mapIndex ? { ...m, [field]: value } : m),
    } : a));
  };

  const removeResponseMapping = (actionIndex: number, mapIndex: number) => {
    setFormActions(prev => prev.map((a, i) => i === actionIndex ? {
      ...a,
      response_mapping: a.response_mapping.filter((_, mi) => mi !== mapIndex),
    } : a));
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading custom connectors...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Custom Connectors</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Build custom API connectors or import from OpenAPI specs
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => { setShowImport(true); setShowCreate(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
              backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
              borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
            }}
          >
            <FileJson size={16} /> Import OpenAPI
          </button>
          <button
            onClick={() => { resetForm(); setShowCreate(true); setShowImport(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
              backgroundColor: ACCENT, color: '#fff', border: 'none',
              borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
            }}
          >
            <Plus size={16} /> New Connector
          </button>
        </div>
      </div>

      {/* Import OpenAPI Modal */}
      {showImport && (
        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>Import from OpenAPI Spec</h2>

          {!importPreview ? (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Connector Name (optional)</label>
                <input
                  value={importName} onChange={e => setImportName(e.target.value)}
                  placeholder="Override the spec title"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Spec URL</label>
                <input
                  value={importUrl} onChange={e => setImportUrl(e.target.value)}
                  placeholder="https://api.example.com/openapi.json"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Or paste JSON spec</label>
                <textarea
                  value={importJson} onChange={e => setImportJson(e.target.value)}
                  rows={6}
                  placeholder='{"openapi": "3.1.0", ...}'
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleImport} disabled={importing || (!importUrl && !importJson)}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: ACCENT, color: '#fff', border: 'none',
                    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                    opacity: importing || (!importUrl && !importJson) ? 0.5 : 1,
                  }}
                >
                  {importing ? 'Parsing...' : 'Parse Spec'}
                </button>
                <button
                  onClick={() => { setShowImport(false); setImportPreview(null); }}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
                    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem',
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ backgroundColor: '#f9fafb', borderRadius: '0.375rem', padding: '1rem', marginBottom: '1rem' }}>
                <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>{importPreview.name}</p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>{importPreview.description}</p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem' }}>Base URL: <code style={{ backgroundColor: '#e5e7eb', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{importPreview.base_url}</code></p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem' }}>Auth: {importPreview.auth.type}</p>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>Actions: {importPreview.actions.length}</p>
              </div>

              {importPreview.actions.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Detected Actions:</p>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {importPreview.actions.map((a, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0', fontSize: '0.813rem' }}>
                        <span style={{
                          backgroundColor: METHOD_COLORS[a.method] || '#6b7280', color: '#fff',
                          padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.688rem', fontWeight: 600, minWidth: 48, textAlign: 'center',
                        }}>
                          {a.method}
                        </span>
                        <code style={{ color: '#374151' }}>{a.path}</code>
                        <span style={{ color: '#6b7280' }}>{a.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleSaveImported} disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                    backgroundColor: '#22c55e', color: '#fff', border: 'none',
                    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                  }}
                >
                  <Save size={16} /> {saving ? 'Saving...' : 'Save Connector'}
                </button>
                <button
                  onClick={() => setImportPreview(null)}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
                    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem',
                  }}
                >
                  Back
                </button>
                <button
                  onClick={() => { setShowImport(false); setImportPreview(null); }}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
                    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem',
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreate && (
        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>
            {editingId ? 'Edit Connector' : 'New Custom Connector'}
          </h2>

          {/* Basic Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Name *</label>
              <input
                value={formName} onChange={e => setFormName(e.target.value)}
                placeholder="My API Connector"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Base URL *</label>
              <input
                value={formBaseUrl} onChange={e => setFormBaseUrl(e.target.value)}
                placeholder="https://api.example.com"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Description</label>
            <input
              value={formDescription} onChange={e => setFormDescription(e.target.value)}
              placeholder="What does this connector do?"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
            />
          </div>

          {/* Auth */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Auth Type</label>
              <select
                value={formAuthType} onChange={e => { setFormAuthType(e.target.value); setFormAuthConfig({}); }}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              >
                {AUTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Status</label>
              <select
                value={formStatus} onChange={e => setFormStatus(e.target.value as 'draft' | 'active' | 'disabled')}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          {/* Auth Config Fields */}
          {formAuthType === 'api_key' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Header Name</label>
                <input
                  value={formAuthConfig.header_name || ''} onChange={e => setFormAuthConfig(prev => ({ ...prev, header_name: e.target.value }))}
                  placeholder="X-Api-Key"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>API Key</label>
                <input
                  type="password"
                  value={formAuthConfig.api_key || ''} onChange={e => setFormAuthConfig(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="your-api-key"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}

          {formAuthType === 'bearer' && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Bearer Token</label>
              <input
                type="password"
                value={formAuthConfig.token || ''} onChange={e => setFormAuthConfig(prev => ({ ...prev, token: e.target.value }))}
                placeholder="your-token"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {formAuthType === 'basic' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Username</label>
                <input
                  value={formAuthConfig.username || ''} onChange={e => setFormAuthConfig(prev => ({ ...prev, username: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Password</label>
                <input
                  type="password"
                  value={formAuthConfig.password || ''} onChange={e => setFormAuthConfig(prev => ({ ...prev, password: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>Actions ({formActions.length})</label>
              <button
                onClick={addAction}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem',
                  backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
                  borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem',
                }}
              >
                <Plus size={14} /> Add Action
              </button>
            </div>

            {formActions.map((action, ai) => (
              <div key={action.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.75rem', marginBottom: '0.5rem', backgroundColor: '#fafafa' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 2fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <select
                    value={action.method} onChange={e => updateAction(ai, 'method', e.target.value)}
                    style={{ padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.813rem', fontWeight: 600 }}
                  >
                    {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    value={action.path} onChange={e => updateAction(ai, 'path', e.target.value)}
                    placeholder="/path"
                    style={{ padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.813rem', fontFamily: 'monospace' }}
                  />
                  <input
                    value={action.name} onChange={e => updateAction(ai, 'name', e.target.value)}
                    placeholder="Action name"
                    style={{ padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.813rem' }}
                  />
                  <button
                    onClick={() => removeAction(ai)}
                    style={{ padding: '0.375rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <input
                  value={action.description} onChange={e => updateAction(ai, 'description', e.target.value)}
                  placeholder="Description (optional)"
                  style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.813rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
                />

                {/* Query Params */}
                <div style={{ marginBottom: '0.375rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>Query Params</span>
                    <button
                      onClick={() => addQueryParam(ai)}
                      style={{ fontSize: '0.688rem', padding: '0.125rem 0.375rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.25rem', cursor: 'pointer' }}
                    >
                      + Add
                    </button>
                  </div>
                  {action.query_params.map((qp, qi) => (
                    <div key={qi} style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                      <input
                        value={qp.name} onChange={e => updateQueryParam(ai, qi, 'name', e.target.value)}
                        placeholder="name" style={{ flex: 2, padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.75rem' }}
                      />
                      <select
                        value={qp.type} onChange={e => updateQueryParam(ai, qi, 'type', e.target.value)}
                        style={{ flex: 1, padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.75rem' }}
                      >
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.125rem', fontSize: '0.688rem' }}>
                        <input type="checkbox" checked={qp.required} onChange={e => updateQueryParam(ai, qi, 'required', e.target.checked)} />
                        Req
                      </label>
                      <button onClick={() => removeQueryParam(ai, qi)} style={{ padding: '0.125rem 0.25rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.688rem' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Response Mapping */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280' }}>Response Mapping</span>
                    <button
                      onClick={() => addResponseMapping(ai)}
                      style={{ fontSize: '0.688rem', padding: '0.125rem 0.375rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.25rem', cursor: 'pointer' }}
                    >
                      + Add
                    </button>
                  </div>
                  {action.response_mapping.map((rm, mi) => (
                    <div key={mi} style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                      <input
                        value={rm.source_path} onChange={e => updateResponseMapping(ai, mi, 'source_path', e.target.value)}
                        placeholder="data.items" style={{ flex: 1, padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}
                      />
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', alignSelf: 'center' }}>-&gt;</span>
                      <input
                        value={rm.target_field} onChange={e => updateResponseMapping(ai, mi, 'target_field', e.target.value)}
                        placeholder="items" style={{ flex: 1, padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}
                      />
                      <button onClick={() => removeResponseMapping(ai, mi)} style={{ padding: '0.125rem 0.25rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.688rem' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCreate} disabled={saving || !formName.trim() || !formBaseUrl.trim()}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                backgroundColor: ACCENT, color: '#fff', border: 'none',
                borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                opacity: saving || !formName.trim() || !formBaseUrl.trim() ? 0.5 : 1,
              }}
            >
              <Save size={16} /> {saving ? 'Saving...' : editingId ? 'Update Connector' : 'Create Connector'}
            </button>
            <button
              onClick={() => { setShowCreate(false); resetForm(); }}
              style={{
                padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
                borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Connectors List */}
      {connectors.length === 0 && !showCreate && !showImport ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <Plug size={48} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
          <p style={{ color: '#6b7280', margin: '0 0 1rem' }}>No custom connectors yet</p>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
            Create a new connector or import from an OpenAPI spec to get started
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {connectors.map(connector => (
            <div key={connector.id} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
              {/* Connector Header */}
              <div
                style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === connector.id ? null : connector.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Plug size={20} style={{ color: ACCENT }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{connector.name}</div>
                    <div style={{ fontSize: '0.813rem', color: '#6b7280' }}>
                      {connector.base_url} | {connector.actions.length} action{connector.actions.length !== 1 ? 's' : ''} |{' '}
                      <span style={{
                        padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.688rem', fontWeight: 600,
                        backgroundColor: connector.status === 'active' ? '#dcfce7' : connector.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                        color: connector.status === 'active' ? '#16a34a' : connector.status === 'draft' ? '#d97706' : '#6b7280',
                      }}>
                        {connector.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {/* Test Results Summary */}
                  {connector.test_results && connector.test_results.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', marginRight: '0.5rem' }}>
                      {connector.test_results.map((r, i) => (
                        <span key={i} title={`${r.action}: ${r.message}`}>
                          {r.success
                            ? <Check size={14} style={{ color: '#22c55e' }} />
                            : <X size={14} style={{ color: '#ef4444' }} />
                          }
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={e => { e.stopPropagation(); handleTest(connector.id); }}
                    disabled={testing === connector.id || connector.actions.length === 0}
                    title="Test all actions"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.75rem',
                      backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                      borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                      opacity: testing === connector.id ? 0.5 : 1,
                    }}
                  >
                    <Play size={12} /> {testing === connector.id ? 'Testing...' : 'Test'}
                  </button>

                  <button
                    onClick={e => { e.stopPropagation(); handleEdit(connector); }}
                    style={{
                      padding: '0.375rem', backgroundColor: '#f3f4f6', color: '#374151',
                      border: '1px solid #d1d5db', borderRadius: '0.25rem', cursor: 'pointer',
                    }}
                  >
                    <Edit2 size={14} />
                  </button>

                  {confirmDelete === connector.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleDelete(connector.id)}
                        style={{ padding: '0.375rem 0.5rem', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.688rem' }}
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        style={{ padding: '0.375rem 0.5rem', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.688rem' }}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={e => { e.stopPropagation(); setConfirmDelete(connector.id); }}
                      style={{
                        padding: '0.375rem', backgroundColor: '#fee2e2', color: '#dc2626',
                        border: '1px solid #fecaca', borderRadius: '0.25rem', cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}

                  {expandedId === connector.id ? <ChevronUp size={16} style={{ color: '#6b7280' }} /> : <ChevronDown size={16} style={{ color: '#6b7280' }} />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === connector.id && (
                <div style={{ borderTop: '1px solid #e5e7eb', padding: '1rem', backgroundColor: '#fafafa' }}>
                  {connector.description && (
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>{connector.description}</p>
                  )}

                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.813rem' }}>
                    <strong>Auth:</strong> {connector.auth.type}
                  </p>

                  <div>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.813rem', fontWeight: 600 }}>Actions:</p>
                    {connector.actions.map((action, i) => {
                      const testResult = connector.test_results?.find(r => r.action === action.name);
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{
                            backgroundColor: METHOD_COLORS[action.method] || '#6b7280', color: '#fff',
                            padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.688rem', fontWeight: 600, minWidth: 48, textAlign: 'center',
                          }}>
                            {action.method}
                          </span>
                          <code style={{ fontSize: '0.813rem', color: '#374151' }}>{action.path}</code>
                          <span style={{ fontSize: '0.813rem', color: '#6b7280', flex: 1 }}>{action.name}</span>
                          {action.query_params.length > 0 && (
                            <span style={{ fontSize: '0.688rem', color: '#9ca3af' }}>{action.query_params.length} params</span>
                          )}
                          {testResult && (
                            <span style={{
                              fontSize: '0.688rem', padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                              backgroundColor: testResult.success ? '#dcfce7' : '#fee2e2',
                              color: testResult.success ? '#16a34a' : '#dc2626',
                            }}>
                              {testResult.message}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
